import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function getDeviceId() {
  let id = localStorage.getItem('tiwistock_device_id');
  if (!id) {
    id = 'dev_' + crypto.randomUUID();
    localStorage.setItem('tiwistock_device_id', id);
  }
  return id;
}

export function usePortfolio() {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const deviceId = getDeviceId();

  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/portfolio/${deviceId}`);
      const data = await res.json();
      if (data.success) {
        setPortfolio(data.data);
        localStorage.setItem('portfolio', JSON.stringify(data.data));
      }
    } catch {
      const saved = localStorage.getItem('portfolio');
      if (saved) setPortfolio(JSON.parse(saved));
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  const addToPortfolio = async (stock, source = 'Manual') => {
    const item = {
      symbol: stock.symbol,
      name: stock.name,
      quantity: 0,
      purchaseDate: new Date().toISOString().split('T')[0],
      source
    };
    const updated = [...portfolio, item];
    setPortfolio(updated);
    localStorage.setItem('portfolio', JSON.stringify(updated));

    try {
      await authFetch(`${API_URL}/api/portfolio/${deviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      });
    } catch (err) {
      console.error('Failed to save to server:', err);
    }
  };

  const removeFromPortfolio = async (symbol) => {
    const updated = portfolio.filter(item => item.symbol !== symbol);
    setPortfolio(updated);
    localStorage.setItem('portfolio', JSON.stringify(updated));

    try {
      await authFetch(`${API_URL}/api/portfolio/${deviceId}/${symbol}`, {
        method: 'DELETE'
      });
    } catch (err) {
      console.error('Failed to remove from server:', err);
    }
  };

  const togglePortfolio = async (stock, source = 'Manual') => {
    if (isInPortfolio(stock.symbol)) {
      await removeFromPortfolio(stock.symbol);
    } else {
      await addToPortfolio(stock, source);
    }
  };

  const updateItem = async (symbol, fields) => {
    const updated = portfolio.map(item =>
      item.symbol === symbol ? { ...item, ...fields } : item
    );
    setPortfolio(updated);
    localStorage.setItem('portfolio', JSON.stringify(updated));

    try {
      await authFetch(`${API_URL}/api/portfolio/${deviceId}/${symbol}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields)
      });
    } catch (err) {
      console.error('Failed to update on server:', err);
    }
  };

  const isInPortfolio = (symbol) => {
    return portfolio.some(item => item.symbol === symbol);
  };

  return {
    portfolio,
    loading,
    addToPortfolio,
    removeFromPortfolio,
    togglePortfolio,
    updateItem,
    isInPortfolio,
    refreshPortfolio: fetchPortfolio
  };
}
